import React, { useState, useRef, useEffect, useCallback } from "react";
import { withSitecoreContext } from "@sitecore-jss/sitecore-jss-react";
import OrderSummary from "../checkout-order-summary";
import ProgressBar from "../../sections/checkout-progress-bar";
import ShippingInfoSummary from "../../sections/shipping-info-summary";
import { MemoryRouter } from "react-router-dom";
import url from "url";
import querystring from "querystring";
import { getGearsOrderSummary, getGearsProgressBars } from "../../services/gearsCheckoutService";
import { clubSelector, selectUserState, staffInfoSelector } from "../../selectors/application";
import { billingInfoSelector, contactInfoSelector, deliverySelector, diffAddrSelector, shippingInfoSelector } from "../../selectors/gears-checkout";
import { useDispatch, useSelector } from "react-redux";
import api from "../../utils/api";
import CheckoutErrorView from "../../sections/checkout-error-view/index";
import { isEmptyObj } from "../../utils/siteUtils";
import { DefaultStoreCode, InstantCode, orderTypes } from "../../utils/lynkco-constants";

import useQuery from "../../utils/useQuery";
import { getDeliveryMethodInfo } from "../../services/orderService";
import { getCart } from "../../services/cartService";
import { appendQuery } from "../../utils/urlHelper";
import PaymentQRCode from "../payment-qr-code";
import { startWatchOrder, stopWatchOrder } from "../../utils/orderWatcher";
import storeApi from "../../utils/storeApi";
import { buildAddress } from "../../services/checkoutService";
import { getApiError } from "../../services/siteService";
import { clearBillingInfoAction, clearShippingInfoAction, clearContactInfoAction, setDiffAddrAction } from "../../redux/actions/gears-checkout";
import { S_LANG, webStorage } from "../../utils/webStorage";
const CheckoutPaymentInStore = ({ page, rendering, fields }) => {
  const { progressBar, selectedBar, summaryItem, shippingAddressHeading, contactInfoHeading, deliveryMethodHeading } = fields;
  const [shippingInfo, setShippingInfo] = useState(null);
  const orderData = useRef();
  const [productsGA, setProductsGA] = useState(null);
  const [orderSummary, setOrderSummary] = useState({});
  const [barProps, setBarProps] = useState({ steps: [] });
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [qrCodeSrc, setQRCodeSrc] = useState(null);
  const lang = webStorage.get(S_LANG);

  /*Redux start*/
  const dispatch = useDispatch();
  const user = useSelector(selectUserState);
  const delivery = useSelector(deliverySelector);
  const billingFormData = useSelector(billingInfoSelector);
  const shippingFormData = useSelector(shippingInfoSelector);
  const contactFormData = useSelector(contactInfoSelector);
  const diffAddr = useSelector(diffAddrSelector);
  const store = useSelector(clubSelector);
  const staffInfo = useSelector(staffInfoSelector);
  const clearBillingFormData = data => dispatch(clearBillingInfoAction(data));
  const clearShippingFormData = data => dispatch(clearShippingInfoAction(data));
  const clearContactFormData = data => dispatch(clearContactInfoAction(data));
  const setDiffAddr = data => dispatch(setDiffAddrAction(data));
  /*Redux end*/

  const query = useQuery();

  const { gearsDeliveryLink, gearsDoneLink } = page.fields;
  useEffect(() => {
    fillShippingRows(delivery).then();
  }, [delivery]);
  useEffect(() => {
    init().then();
    return () => {
      stopWatchOrder();
    };
  }, []);
  const isInstant = useCallback(() => {
    return delivery.code === InstantCode;
  }, [delivery]);
  const init = async () => {
    const value = await getGearsProgressBars(progressBar, selectedBar);
    if (value) setBarProps(value);
    const obj = await getCart(lang);
    const { skuList, afterDiscountTotalPrice } = obj.obj;
    const cartQtyList = skuList.map(x => ({
      skuId: x.skuId,
      qty: x.skuCount
    }));
    const { obj: promotions } = await api.user.promotion(null, {
      skuIds: cartQtyList.map(e => e.skuId)
    });
    const [_orderData, summary] = await getGearsOrderSummary(
      afterDiscountTotalPrice,
      {
        deliveryId: delivery?.id,
        skuQtyVoList: cartQtyList,
        promotions
      },
      summaryItem,
      true
    );
    setOrderSummary(summary);
    orderData.current = _orderData;
    await submitOrder(cartQtyList);
    // /** Measurements */
    let dataLayer = window.dataLayer || [];
    let products = [];
    _orderData.orderItems.forEach(item => {
      let product = {
        name: item.spuName.toLowerCase().replace(/ /g, "-"),
        // id: "product_sku",
        price: item.paymentAmt, // Product price
        quantity: item.qty // Product quantity
        // brand: "product_brand", // Product brand
        // category: "Level_1/Level_2/Level_3", // Product category (max. 5 levels)
        // variant: "product_variant"
      };
      products.push(product);
    });
    dataLayer.push({
      event: "checkout",
      step: 3,
      products: products
    });
    setProductsGA(products);
  };
  const fillShippingRows = async delivery => {
    const deliveryDict = await getDeliveryMethodInfo(delivery.code);
    let contactRows = [];
    let shippingRows = shippingFormData ? [`${shippingFormData.firstName} ${shippingFormData.lastName}`, `${shippingFormData.street} ${shippingFormData.apartment}`, `${shippingFormData.postalCode} ${shippingFormData.city}`] : [];
    if (isInstant()) {
      contactRows = user ? [user.email, `${user.mobileNoCountryCode}${user.mobileNo}`] : [];
    } else {
      contactRows = contactFormData ? [contactFormData.email, `${contactFormData.phoneArea}${contactFormData.phone}`] : [];
    }
    const shippingInfo = {
      deliveryMethod: {
        heading: deliveryMethodHeading.value,
        rows: [deliveryDict?.dicName],
        value: delivery?.cost,
        link: { url: gearsDeliveryLink.value.href }
      }
    };
    if (contactRows.length > 0) {
      Object.assign(shippingInfo, {
        contactInfo: {
          heading: contactInfoHeading.value,
          rows: contactRows,
          link: { url: gearsDeliveryLink.value.href }
        }
      });
    }
    if (!isInstant()) {
      Object.assign(shippingInfo, {
        shippingAddress: {
          heading: shippingAddressHeading.value,
          rows: shippingRows,
          link: { url: gearsDeliveryLink.value.href }
        }
      });
    }
    setShippingInfo(shippingInfo);
  };
  const submitOrder = async cartQtyList => {
    try {
      let orderNo = query?.orderNo;
      let orderId = query?.orderId;
      if (!orderNo) {
        const saveBody = {
          promotions: orderData.current.promotions,
          deliveryId: delivery.id,
          skuQtyVoList: cartQtyList,
          subType: orderTypes.gear,
          storeCode: store?.code || DefaultStoreCode,
          deliveryStoreCode: store?.code || DefaultStoreCode,
          staff: staffInfo.account.userName
        };
        if (!isInstant()) {
          const phoneAreaCode = contactFormData.phoneArea ? contactFormData.phoneArea : "";
          const phone = contactFormData.phone;
          const mobile = `${phoneAreaCode}${phone}`;
          Object.assign(saveBody, {
            contactEmail: contactFormData.email,
            contactMobile: mobile
          });
          if (diffAddr) {
            saveBody.billingAddress = buildAddress("billing", billingFormData, contactFormData, diffAddr);
            saveBody.receiverInfoAddress = buildAddress("receiver", shippingFormData, contactFormData, diffAddr);
          } else {
            saveBody.billingAddress = buildAddress("billing", shippingFormData, contactFormData, diffAddr);
            saveBody.receiverInfoAddress = buildAddress("receiver", shippingFormData, contactFormData, diffAddr);
          }
        } else if (!isEmptyObj(user)) {
          const phoneAreaCode = user?.mobileNoCountryCode ? user.mobileNoCountryCode : "";
          const phone = user?.mobileNo;
          const mobile = `${phoneAreaCode}${phone}`;
          Object.assign(saveBody, {
            contactEmail: user?.email,
            contactMobile: mobile
          });
        }
        if (user?.userAccount) {
          saveBody.customerAccount = user?.userAccount;
        }
        // console.log("saveBody", saveBody);
        const { obj } = await storeApi.order.save(null, saveBody);
        orderNo = obj.orderNo;
        orderId = obj.orderId;
      }

      const doneLink = url.parse(gearsDoneLink.value.href);
      const doneLinkQuery = querystring.parse(doneLink.query);
      const redirect = decodeURIComponent(doneLinkQuery.redirect);
      const nextUrl = appendQuery(redirect, {
        orderNo: orderNo,
        orderId: orderId,
        inStore: true
      });
      const returnUrl = decodeURIComponent(
        appendQuery(gearsDoneLink.value.href, {
          ...doneLinkQuery,
          redirect: encodeURIComponent(nextUrl)
        })
      );
      const {
        obj: { redirectUrl }
      } = await storeApi.order.pay(null, {
        orderNo: orderNo, //订单号
        isRemember: 0, //此项目勾选可以保存本次paymenttoken
        returnUrl: returnUrl
      });
      setQRCodeSrc(redirectUrl);
      await startWatchOrder({
        redirect: nextUrl,
        orderId,
        orderNo,
        onSuccess: () => {
          clearBillingFormData();
          clearShippingFormData();
          clearContactFormData();
          setDiffAddr(null);
        },
        onError: handleError
      });
      // /** Measurements */
      /*let dataLayer = window.dataLayer || [];
      let event = {
        event: "purchase",
        transaction: {
          id: orderId || orderNo,
          // affiliation: iframeUrl, // Website on which order was made
          revenue: amount,
          tax: orderData?.tax,
          shipping: orderData?.shipping,
          coupon: orderData?.promotions
        },
        products: productsGA
      };*/
    } catch (ex) {
      console.error(ex);
      handleError(ex);
    }
  };
  const handleError = async ex => {
    console.log("ex", ex);
    const error = await getApiError(ex?.code);
    setError(error);
    setShowError(true);
  };
  const errorViewProps = {
    error,
    showError,
    onClose: () => {
      setShowError(false);
    }
  };
  return (
    <section className="checkout-payment grid" datasourceid={rendering.dataSource}>
      <MemoryRouter>
        <ProgressBar {...barProps} />
      </MemoryRouter>
      {shippingInfo && <ShippingInfoSummary {...{ ...shippingInfo }} />}
      <PaymentQRCode {...{ qrCodeSrc }} />
      <div className="summary-section">
        <OrderSummary {...orderSummary} />
      </div>
      {error && <CheckoutErrorView {...errorViewProps} />}
    </section>
  );
};
export default withSitecoreContext()(CheckoutPaymentInStore);
